import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Save, Play, AlertTriangle, Info, Pencil, Check, X, Lock } from 'lucide-react';
import { api } from '../lib/api';

// ── Local DSL v2 types (mirrors packages/shared/src/types/workflow.ts) ─────────
type JourneyNodeType =
  | 'trigger'
  | 'send_email'
  | 'send_sms'
  | 'send_campaign'
  | 'wait_duration'
  | 'wait_event'
  | 'condition'
  | 'add_points'
  | 'remove_points'
  | 'issue_coupon'
  | 'exit'
  | 'update_attribute';

interface JourneyDefinitionV2 {
  version: '2.0';
  entryNodeId: string;
  nodes: Array<{
    id: string;
    type: JourneyNodeType;
    name?: string;
    config: Record<string, unknown>;
    position: { x: number; y: number };
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    label?: string;
  }>;
}
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import NodePalette from '../components/journey/NodePalette';
import NodeConfigPanel from '../components/journey/NodeConfigPanel';
import TriggerNode from '../components/journey/nodes/TriggerNode';
import SendEmailNode from '../components/journey/nodes/SendEmailNode';
import SendSmsNode from '../components/journey/nodes/SendSmsNode';
import WaitDurationNode from '../components/journey/nodes/WaitDurationNode';
import WaitEventNode from '../components/journey/nodes/WaitEventNode';
import ConditionNode from '../components/journey/nodes/ConditionNode';
import PointsNode from '../components/journey/nodes/PointsNode';
import IssueCouponNode from '../components/journey/nodes/IssueCouponNode';
import ExitNode from '../components/journey/nodes/ExitNode';
import SendCampaignNode from '../components/journey/nodes/SendCampaignNode';
import UpdateAttributeNode from '../components/journey/nodes/UpdateAttributeNode';

// ── Node type registry ────────────────────────────────────────────────────────
const NODE_TYPES: NodeTypes = {
  trigger: TriggerNode,
  send_email: SendEmailNode,
  send_sms: SendSmsNode,
  send_campaign: SendCampaignNode,
  wait_duration: WaitDurationNode,
  wait_event: WaitEventNode,
  condition: ConditionNode,
  add_points: PointsNode,
  remove_points: PointsNode,
  issue_coupon: IssueCouponNode,
  exit: ExitNode,
  update_attribute: UpdateAttributeNode,
};

// ── API types (minimal) ───────────────────────────────────────────────────────
interface AutomationDetail {
  id: string;
  name: string;
  type: string;
  status: string;
  definition: Record<string, unknown> | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
let nodeIdCounter = 1;
function newId() {
  return `node_${Date.now()}_${nodeIdCounter++}`;
}

/** Convert DSL v2 → React Flow nodes */
function dslToFlow(def: JourneyDefinitionV2): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = def.nodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position,
    data: { ...n, config: n.config ?? {} },
  }));
  const edges: Edge[] = def.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    sourceHandle: e.label === 'true' ? 'true' : e.label === 'false' ? 'false' : undefined,
  }));
  return { nodes, edges };
}

/** Convert a legacy v1 DSL to a simple linear sequence of RF nodes */
function legacyToFlow(def: Record<string, unknown>): { nodes: Node[]; edges: Edge[] } {
  const steps = (def.steps as Array<Record<string, unknown>>) ?? [];
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Trigger node
  const triggerId = 'trigger_0';
  nodes.push({
    id: triggerId,
    type: 'trigger',
    position: { x: 200, y: 50 },
    data: {
      id: triggerId,
      type: 'trigger',
      config: def.trigger ?? {},
    },
  });

  let prevId = triggerId;
  steps.forEach((step, i) => {
    const id = `step_${i}`;
    nodes.push({
      id,
      type: (step.type as string) ?? 'send_email',
      position: { x: 200, y: 180 + i * 130 },
      data: { id, type: step.type, config: step },
    });
    edges.push({
      id: `e_${prevId}_${id}`,
      source: prevId,
      target: id,
    });
    prevId = id;
  });

  return { nodes, edges };
}

/** Build DSL v2 from current RF state */
function buildDSL(nodes: Node[], edges: Edge[]): JourneyDefinitionV2 {
  const entryNode = nodes.find((n) => n.type === 'trigger');
  return {
    version: '2.0',
    entryNodeId: entryNode?.id ?? '',
    nodes: nodes.map((n) => ({
      id: n.id,
      type: n.type as JourneyNodeType,
      name: (n.data.name as string) ?? undefined,
      config: (n.data.config as Record<string, unknown>) ?? {},
      position: n.position,
    })),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label as string | undefined,
    })),
  };
}

/** Validate the graph before publishing */
function validate(nodes: Node[], edges: Edge[]): string[] {
  const errors: string[] = [];

  const triggers = nodes.filter((n) => n.type === 'trigger');
  if (triggers.length === 0) errors.push('The automation must have exactly one Trigger node.');
  if (triggers.length > 1)
    errors.push(`Found ${triggers.length} Trigger nodes — only one is allowed.`);

  const isolated = nodes.filter(
    (n) => n.type !== 'trigger' && !edges.some((e) => e.source === n.id || e.target === n.id),
  );
  if (isolated.length > 0)
    errors.push(`${isolated.length} node(s) are isolated (not connected to the graph).`);

  const conditionNodes = nodes.filter((n) => n.type === 'condition');
  conditionNodes.forEach((cn) => {
    const outEdges = edges.filter((e) => e.source === cn.id);
    if (outEdges.length !== 2)
      errors.push(
        `Condition node "${cn.data.name ?? cn.id}" must have exactly 2 outgoing edges (true + false).`,
      );
  });

  const nonExit = nodes.filter((n) => n.type !== 'exit' && n.type !== 'condition');
  nonExit.forEach((n) => {
    const out = edges.filter((e) => e.source === n.id);
    if (out.length === 0 && n.type !== 'trigger')
      errors.push(`Node "${n.data.name ?? n.id}" has no outgoing connection.`);
  });

  return errors;
}

// ── Inner canvas (needs ReactFlowProvider context) ────────────────────────────
function JourneyCanvas({
  initialNodes,
  initialEdges,
  onSave,
  onPublish,
  isSaving,
  isPublishing,
  automationName,
  automationStatus,
  isLegacy,
  onRename,
  isRenaming,
  readOnly,
}: {
  initialNodes: Node[];
  initialEdges: Edge[];
  onSave: (nodes: Node[], edges: Edge[]) => void;
  onPublish: (nodes: Node[], edges: Edge[]) => void;
  isSaving: boolean;
  isPublishing: boolean;
  automationName: string;
  automationStatus: string;
  isLegacy: boolean;
  onRename: (name: string) => void;
  isRenaming: boolean;
  readOnly: boolean;
}) {
  const navigate = useNavigate();
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [nodeToDelete, setNodeToDelete] = useState<Node | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showLegacyBanner, setShowLegacyBanner] = useState(isLegacy);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Inline name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(automationName);

  // Keep draftName in sync if parent refreshes the name (e.g. after save)
  useEffect(() => {
    if (!isEditingName) setDraftName(automationName);
  }, [automationName, isEditingName]);

  const commitRename = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== automationName) onRename(trimmed);
    setIsEditingName(false);
  };

  const cancelRename = () => {
    setDraftName(automationName);
    setIsEditingName(false);
  };

  // Keep selectedNode in sync with node state changes
  useEffect(() => {
    if (selectedNode) {
      const updated = nodes.find((n) => n.id === selectedNode.id);
      if (updated) setSelectedNode(updated);
    }
  }, [nodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Delete key handler — opens confirmation dialog for non-trigger nodes
  useEffect(() => {
    if (readOnly) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Delete' && selectedNode !== null && selectedNode.type !== 'trigger') {
        setNodeToDelete(selectedNode);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedNode, readOnly]);

  const onConnect = useCallback(
    (connection: Connection) =>
      setEdges((eds) => {
        // When a connection originates from a named source handle (condition branches,
        // wait_event timeout), use the handle id as the edge label so the journey
        // compiler can find the "true" / "false" / "timeout" branches.
        const edgeAttrs =
          connection.sourceHandle === 'true' ||
          connection.sourceHandle === 'false' ||
          connection.sourceHandle === 'timeout'
            ? { label: connection.sourceHandle }
            : {};
        return addEdge({ ...connection, ...edgeAttrs }, eds);
      }),
    [setEdges],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData('application/reactflow') as JourneyNodeType;
      if (!nodeType) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const id = newId();
      const newNode: Node = {
        id,
        type: nodeType,
        position,
        data: { id, type: nodeType, config: {}, name: '' },
      };

      setNodes((nds) => [...nds, newNode]);
      setSelectedNode(newNode);
    },
    [screenToFlowPosition, setNodes],
  );

  function handleConfigChange(nodeId: string, config: Record<string, unknown>) {
    // __name is a special key routed to node.data.name
    const { __name, ...rest } = config as Record<string, unknown> & { __name?: string };
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, config: rest, name: __name ?? n.data.name } }
          : n,
      ),
    );
  }

  function handleSave() {
    setValidationErrors([]);
    onSave(nodes, edges);
  }

  function handlePublish() {
    const errors = validate(nodes, edges);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    onPublish(nodes, edges);
  }

  const statusColors: Record<string, string> = {
    draft: 'bg-amber-100 text-amber-700',
    active: 'bg-emerald-100 text-emerald-700',
    paused: 'bg-amber-100 text-amber-700',
    archived: 'bg-zinc-100 text-zinc-500',
  };

  return (
    <div className="flex flex-col h-screen w-screen bg-zinc-50 overflow-hidden">
      {/* ── Header ── */}
      <header className="h-12 shrink-0 flex items-center gap-3 px-4 bg-white border-b border-zinc-200 z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-zinc-500 hover:text-zinc-800 text-sm transition-colors"
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="w-px h-4 bg-zinc-200" />
        {/* ── Inline name editor ── */}
        {isEditingName ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') cancelRename();
              }}
              className="text-sm font-semibold text-zinc-800 border border-zinc-300 rounded px-2 py-0.5 w-52 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <button
              onClick={commitRename}
              disabled={isRenaming}
              className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
              title="Confirm"
            >
              <Check size={14} />
            </button>
            <button
              onClick={cancelRename}
              className="p-1 text-zinc-400 hover:text-zinc-600"
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditingName(true)}
            className="flex items-center gap-1.5 group"
            title="Rename automation"
          >
            <span className="text-sm font-semibold text-zinc-800 truncate max-w-xs">
              {automationName}
            </span>
            <Pencil
              size={11}
              className="text-zinc-300 group-hover:text-zinc-500 transition-colors shrink-0"
            />
          </button>
        )}
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${
            statusColors[automationStatus] ?? 'bg-zinc-100 text-zinc-500'
          }`}
        >
          {automationStatus}
        </span>
        <div className="flex-1" />
        {readOnly ? (
          <span className="flex items-center gap-1.5 text-xs text-zinc-400 font-medium">
            <Lock size={13} />
            Read-only
          </span>
        ) : (
          <>
            {validationErrors.length > 0 && (
              <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                <AlertTriangle size={13} />
                {validationErrors.length} validation error{validationErrors.length > 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
            >
              <Save size={13} />
              {isSaving ? 'Saving…' : 'Save Draft'}
            </button>
            {automationStatus === 'draft' && (
              <button
                onClick={handlePublish}
                disabled={isPublishing}
                className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                <Play size={13} />
                {isPublishing ? 'Publishing…' : 'Publish'}
              </button>
            )}
          </>
        )}
      </header>

      {/* ── Validation errors banner ── */}
      {validationErrors.length > 0 && (
        <div className="shrink-0 bg-red-50 border-b border-red-200 px-4 py-2 flex flex-col gap-0.5">
          {validationErrors.map((e, i) => (
            <p key={i} className="text-xs text-red-700 flex items-start gap-1.5">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />
              {e}
            </p>
          ))}
        </div>
      )}

      {/* ── Read-only banner ── */}
      {readOnly && (
        <div className="shrink-0 bg-zinc-50 border-b border-zinc-200 px-4 py-2 flex items-center gap-2">
          <Lock size={13} className="text-zinc-400 shrink-0" />
          <p className="text-xs text-zinc-500">
            This automation is <strong>{automationStatus}</strong> — viewing in read-only mode. Deactivate it to edit.
          </p>
        </div>
      )}

      {/* ── Legacy banner ── */}
      {showLegacyBanner && (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
          <Info size={14} className="text-amber-600 shrink-0" />
          <p className="text-xs text-amber-700 flex-1">
            This automation was created with the old format and has been converted to the visual
            builder. Save to persist the new format.
          </p>
          <button
            onClick={() => setShowLegacyBanner(false)}
            className="text-xs text-amber-600 hover:text-amber-800 font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Main area ── */}
      <div className="flex flex-1 overflow-hidden" ref={reactFlowWrapper}>
        {/* Palette — hidden in read-only */}
        {!readOnly && <NodePalette />}

        {/* Canvas */}
        <div className="flex-1 h-full" onDragOver={readOnly ? undefined : onDragOver} onDrop={readOnly ? undefined : onDrop}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            onNodeClick={readOnly ? undefined : onNodeClick}
            onPaneClick={onPaneClick}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
            fitView
            deleteKeyCode={null}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} size={1} color="#e4e4e7" />
            <Controls />
            <MiniMap
              nodeColor={(n) => {
                const colors: Record<string, string> = {
                  trigger: '#10b981',
                  send_email: '#6366f1',
                  send_sms: '#8b5cf6',
                  send_campaign: '#06b6d4',
                  wait_duration: '#f59e0b',
                  wait_event: '#fbbf24',
                  condition: '#71717a',
                  add_points: '#34d399',
                  remove_points: '#34d399',
                  issue_coupon: '#f97316',
                  exit: '#ef4444',
                };
                return colors[n.type ?? ''] ?? '#a1a1aa';
              }}
              maskColor="rgba(244,244,245,0.8)"
            />
          </ReactFlow>
        </div>

        {/* Config panel — hidden in read-only */}
        {!readOnly && selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onChange={handleConfigChange}
            onClose={() => setSelectedNode(null)}
            onDelete={() => setNodeToDelete(selectedNode)}
          />
        )}
      </div>

      {/* Node deletion confirmation */}
      <ConfirmDialog
        open={nodeToDelete !== null}
        title="Eliminare questo nodo?"
        message="Tutte le connessioni collegate verranno rimosse."
        variant="danger"
        onConfirm={() => {
          if (nodeToDelete) {
            setNodes((nds) => nds.filter((n) => n.id !== nodeToDelete.id));
            setEdges((eds) =>
              eds.filter((e) => e.source !== nodeToDelete.id && e.target !== nodeToDelete.id),
            );
            setSelectedNode(null);
            setNodeToDelete(null);
          }
        }}
        onCancel={() => setNodeToDelete(null)}
      />
    </div>
  );
}

// ── Page (data loader) ────────────────────────────────────────────────────────
export default function JourneyBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: automation, isLoading } = useQuery<AutomationDetail>({
    queryKey: ['automation', id],
    queryFn: () => api.get(`/api/v1/automations/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const saveMutation = useMutation({
    mutationFn: (dsl: JourneyDefinitionV2) =>
      api.post(`/api/v1/automations/${id}/versions`, { definitionJson: dsl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', id] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (dsl: JourneyDefinitionV2) => {
      await api.post(`/api/v1/automations/${id}/versions`, { definitionJson: dsl });
      await api.post(`/api/v1/automations/${id}/publish`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', id] });
      navigate(`/automations/${id}`);
    },
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => api.patch(`/api/v1/automations/${id}`, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['automation', id] }),
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-400">Loading automation…</div>
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Automation not found.</div>
      </div>
    );
  }

  // Determine initial canvas state
  const def = automation.definition as Record<string, unknown> | null;
  let initialNodes: Node[] = [];
  let initialEdges: Edge[] = [];
  let isLegacy = false;

  if (def && (def as { version?: string }).version === '2.0') {
    const result = dslToFlow(def as unknown as JourneyDefinitionV2);
    initialNodes = result.nodes;
    initialEdges = result.edges;
  } else if (def && def.steps) {
    const result = legacyToFlow(def);
    initialNodes = result.nodes;
    initialEdges = result.edges;
    isLegacy = true;
  } else {
    // Empty canvas — pre-place a trigger node
    initialNodes = [
      {
        id: 'trigger_initial',
        type: 'trigger',
        position: { x: 200, y: 100 },
        data: { id: 'trigger_initial', type: 'trigger', config: {}, name: '' },
      },
    ];
  }

  const readOnly = automation.status !== 'draft';

  return (
    <ReactFlowProvider>
      <JourneyCanvas
        initialNodes={initialNodes}
        initialEdges={initialEdges}
        automationName={automation.name}
        automationStatus={automation.status}
        isLegacy={isLegacy}
        isSaving={saveMutation.isPending}
        isPublishing={publishMutation.isPending}
        onSave={(nodes, edges) => saveMutation.mutate(buildDSL(nodes, edges))}
        onPublish={(nodes, edges) => publishMutation.mutate(buildDSL(nodes, edges))}
        onRename={(name) => renameMutation.mutate(name)}
        isRenaming={renameMutation.isPending}
        readOnly={readOnly}
      />
    </ReactFlowProvider>
  );
}
